import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, switchMap } from 'rxjs/operators';
import { openConfirmDialog } from 'app/components/common/confirm-dialog/confirm-dialog.component';
import { GridStateCapture, TemplateViewService, isErrorRow } from 'app/services/template-view.service';
import { TemplateViewRow, TemplateViewShare, TemplateViewVisibility, UserDirectoryEntry, initialsOf } from 'app/models/template-view';
import { TOAST_SHORT, TOAST_LONG, TOAST_XLONG, deleteViewConfirm, errorRowMessage } from '../manage-views.shared';

export interface ViewSettingsDialogData {
  mode: 'new' | 'edit';
  pageCode: string;
  view?: TemplateViewRow;                  // edit mode: the list row
  captureState?: () => GridStateCapture;   // new mode: current grid state
  isAdmin: boolean;
  existingNames: string[];
}

export interface ViewSettingsDialogResult {
  createdId?: number;
  changed?: boolean;
  deleted?: boolean;
  /** The enriched row returned by the save proc — callers apply it locally
   *  instead of refetching the whole list. */
  row?: TemplateViewRow;
}

@Component({
  selector: 'app-view-settings-dialog',
  templateUrl: './view-settings-dialog.component.html',
  styleUrls: ['./view-settings-dialog.component.scss'],
  standalone: false,
})
export class ViewSettingsDialogComponent implements OnInit {

  title = 'New view';
  name = '';
  description = '';
  visibility: TemplateViewVisibility = 'Private';
  ownerName = 'You';

  canEdit = false;    // may save at all (owner / admin-on-global-public / editor)
  canManage = false;  // owner or admin-on-global-public: visibility, collaborators, delete
  saving = false;
  nameIsDuplicate = false;

  shares: TemplateViewShare[] = [];
  private originalShares: TemplateViewShare[] = [];
  loadingShares = false;

  directory: UserDirectoryEntry[] = [];
  filteredUsers: UserDirectoryEntry[] = [];
  loadingDirectory = false;
  addQuery = '';
  addRole: 'Viewer' | 'Editor' = 'Viewer';

  constructor(
    public dialogRef: MatDialogRef<ViewSettingsDialogComponent, ViewSettingsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ViewSettingsDialogData,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private svc: TemplateViewService
  ) {}

  ngOnInit(): void {
    if (this.data.mode === 'new') {
      this.title = 'New view';
      this.canEdit = true;
      this.canManage = true;
    } else {
      const v = this.data.view;
      this.title = v.templateViewName;
      this.name = v.templateViewName || '';
      this.description = v.description || '';
      this.visibility = this.svc.visibilityOf(v);
      this.ownerName = v.isOwner === 1 ? 'You' : (v.ownerName || '');
      this.canEdit = v.canEdit === 1;
      // Prefer the server-computed CanManage (single source of the rule);
      // fall back to the local derivation for legacy cached rows.
      this.canManage = v.canManage !== undefined
        ? v.canManage === 1
        : (v.isOwner === 1
            || (this.data.isAdmin && (this.visibility === 'Global' || this.visibility === 'Public')));
      this.loadShares(v.templateViewID);
    }

    if (this.canManage) {
      this.loadingDirectory = true;
      this.svc.userDirectory().subscribe({
        next: users => { this.directory = users; this.loadingDirectory = false; this.filterUsers(); },
        error: () => { this.directory = []; this.loadingDirectory = false; }
      });
    }
  }

  get visOptions(): TemplateViewVisibility[] {
    const options: TemplateViewVisibility[] = ['Private', 'Shared', 'Public'];
    if (this.data.isAdmin) options.push('Global');
    return options;
  }

  // ── the one share block, relabeled per visibility ────────────────────────
  // Shared: full collaborator list with per-person roles.
  // Public: named editors only (everyone can already view).

  get showShareBlock(): boolean {
    return this.visibility === 'Shared' || this.visibility === 'Public';
  }

  get showRoleSelect(): boolean { return this.visibility === 'Shared'; }

  get shareHeading(): string {
    return this.visibility === 'Shared' ? 'Collaborators' : 'Editors';
  }

  get shareHint(): string {
    return this.visibility === 'Shared' ? '' : '— everyone else can view';
  }

  get shareEmptyText(): string {
    return this.visibility === 'Shared'
      ? 'Not shared with anyone yet.'
      : 'No editors yet — everyone can view.';
  }

  get sharePickerLabel(): string {
    return this.visibility === 'Shared'
      ? 'Add people by name or email'
      : 'Add editors by name or email';
  }

  /** Role granted when a person is picked (Public mode always adds editors). */
  get pickRole(): 'Viewer' | 'Editor' {
    return this.visibility === 'Shared' ? this.addRole : 'Editor';
  }

  /** Rows displayed in the current mode (Public mode shows only editors). */
  get visibleShares(): TemplateViewShare[] {
    return this.visibility === 'Public'
      ? this.shares.filter(s => s.accessLevel === 'Editor')
      : this.shares;
  }

  get canDelete(): boolean {
    return this.data.mode === 'edit' && this.canManage;
  }

  get saveLabel(): string { return this.data.mode === 'new' ? 'Create' : 'Save'; }

  initials(name: string): string { return initialsOf(name); }

  onNameChange() {
    const trimmed = (this.name || '').trim().toLowerCase();
    this.nameIsDuplicate = !!trimmed
      && this.data.existingNames.some(n => (n || '').trim().toLowerCase() === trimmed);
  }

  private loadShares(templateViewID: number) {
    this.loadingShares = true;
    this.svc.shareList(templateViewID).subscribe({
      next: rows => {
        this.shares = rows;
        this.originalShares = rows.map(r => ({ ...r }));
        this.loadingShares = false;
        this.filterUsers();
      },
      error: () => {
        this.loadingShares = false;
        this.snackBar.open('Could not load the collaborator list.', 'Close', { duration: TOAST_LONG });
      }
    });
  }

  // ── people picker ────────────────────────────────────────────────────────

  filterUsers() {
    const q = (this.addQuery || '').trim().toLowerCase();
    const taken = new Set(this.shares.map(s => s.internalContactsID));
    const ownerId = this.data.mode === 'edit'
      ? this.data.view?.createdByID
      : this.svc.currentUserId();
    this.filteredUsers = this.directory
      .filter(u => u.internalContactsID !== ownerId && !taken.has(u.internalContactsID))
      .filter(u => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }

  pickUser(user: UserDirectoryEntry, role: 'Viewer' | 'Editor') {
    if (!user) return;
    this.shares.push({
      internalContactsID: user.internalContactsID,
      name: user.name,
      email: user.email,
      accessLevel: role
    });
    // clear after the autocomplete writes its value into the input
    setTimeout(() => { this.addQuery = ''; this.filterUsers(); });
  }

  displayNothing(): string { return ''; }

  removeShare(share: TemplateViewShare) {
    this.shares = this.shares.filter(s => s !== share);
    this.filterUsers();
  }

  // ── actions ──────────────────────────────────────────────────────────────

  cancel() {
    this.dialogRef.close();
  }

  save() {
    const trimmedName = (this.name || '').trim();
    if (!trimmedName || this.saving) return;
    this.saving = true;
    if (this.data.mode === 'new') this.saveNew(trimmedName);
    else this.saveEdit(trimmedName);
  }

  private saveNew(trimmedName: string) {
    const state = this.data.captureState
      ? this.data.captureState()
      : { columnState: '[]', filterState: '{}', sortState: '[]', groupState: '[]' };
    this.svc.save({
      templateViewID: 0,
      pageCode: this.data.pageCode,
      templateViewName: trimmedName,
      description: this.description,
      visibility: this.visibility,
      columnState: state.columnState,
      filterState: state.filterState,
      sortState: state.sortState,
      groupState: state.groupState,
      status: 'Active'
    }).subscribe({
      next: row => this.handleSaveResult(row, "You don't have permission to create this view.", () => {
        this.snackBar.open(`“${trimmedName}” created`, 'Close', { duration: TOAST_SHORT });
        this.dialogRef.close({ createdId: row.templateViewID, row });
      }),
      error: () => this.saveFailed()
    });
  }

  // Edit mode — the layout states are OMITTED entirely: the v2 proc preserves
  // the stored layout for absent state params, so a rename/re-share can never
  // clobber a layout (not even with a stale client snapshot).
  private saveEdit(trimmedName: string) {
    const v = this.data.view;
    this.svc.save({
      templateViewID: v.templateViewID,
      pageCode: v.pageCode,
      templateViewName: trimmedName,
      description: this.description,
      visibility: this.canManage ? this.visibility : this.svc.visibilityOf(v),
      pivotView: v.pivotView,
      templateSortOrder: v.templateSortOrder,
      districtID: v.districtID,
      status: 'Active'
    }).subscribe({
      next: row => this.handleSaveResult(row, "You don't have permission to change this view.", () => {
        this.snackBar.open(`“${trimmedName}” saved`, 'Close', { duration: TOAST_SHORT });
        this.dialogRef.close({ changed: true, row });
      }),
      error: () => this.saveFailed()
    });
  }

  /** Shared tail of both save paths: sentinel check → share diff → close. */
  private handleSaveResult(row: TemplateViewRow, deniedMsg: string, closeWithSuccess: () => void) {
    if (isErrorRow(row)) {
      this.saving = false;
      this.snackBar.open(errorRowMessage(row, deniedMsg), 'Close', { duration: TOAST_LONG });
      return;
    }
    this.applyShares(row.templateViewID, row, closeWithSuccess);
  }

  private saveFailed() {
    this.saving = false;
    this.snackBar.open('Error saving the view. Please try again.', 'Close', { duration: TOAST_LONG });
  }

  /** Applies the local share edits, respecting the final visibility. */
  private applyShares(templateViewID: number, savedRow: TemplateViewRow, done: () => void) {
    if (!this.canManage) { done(); return; }

    let target: TemplateViewShare[];
    if (this.visibility === 'Shared') {
      target = this.shares;
    } else if (this.visibility === 'Public') {
      target = this.shares.filter(s => s.accessLevel === 'Editor');
    } else {
      // Private/Global: leave existing share rows untouched (dormant) —
      // switching back to Shared restores the collaborator list.
      done();
      return;
    }

    this.svc.applyShareDiff(templateViewID, this.originalShares, target).subscribe({
      next: () => done(),
      error: () => {
        this.saving = false;
        this.snackBar.open('The view was saved but sharing changes failed. Please reopen and try again.', 'Close', { duration: TOAST_XLONG });
        // In create mode the view DOES exist — close with its id so the caller
        // still selects it (otherwise the created view silently vanishes from
        // the flow and the user creates a duplicate).
        this.dialogRef.close(this.data.mode === 'new'
          ? { createdId: templateViewID, changed: true, row: savedRow }
          : { changed: true, row: savedRow });
      }
    });
  }

  deleteView() {
    const v = this.data.view;
    openConfirmDialog(this.dialog, deleteViewConfirm(v.templateViewName)).pipe(
      filter(Boolean),
      switchMap(() => this.svc.deleteView(v))
    ).subscribe({
      next: row => {
        if (isErrorRow(row)) {
          this.snackBar.open(errorRowMessage(row, "You don't have permission to delete this view."),
            'Close', { duration: TOAST_LONG });
          return;
        }
        this.snackBar.open(`“${v.templateViewName}” deleted`, 'Close', { duration: TOAST_SHORT });
        this.dialogRef.close({ deleted: true });
      },
      error: () => this.snackBar.open('Error deleting the view. Please try again.', 'Close', { duration: TOAST_LONG })
    });
  }
}
